import React, { useEffect, useMemo, useState, useCallback } from 'react';
import s from './AudienceInsights.module.css';
import BrandVoiceChart from './charts/BrandVoiceChart';
import PostTypeEngagementChart from './charts/PostTypeEngagementChart';
import AudienceGrowthDonut from './charts/AudienceGrowthDonut';
import DropDownMenu from '../../../shared/ui/dropdown-menu/DropDownMenu';
import IconCalendar from '../../../shared/ui/icons/IconCalendar';
import { socialMediaOptions } from '../../../shared/constant/constant';
import {
    fetchBrandVoiceGraph,
    fetchFollowersGraph,
    fetchWeeklyStats,
} from '../../../shared/api/dashboardApi';
import { useAppDispatch } from '../../../store/store';
import { useSelector } from 'react-redux';
import {
    selectBrandVoiceGraph,
    selectFollowersGraph,
} from '../../../store/selectors/dashboardSelectors';
import { BrandVoiceGraph } from '../../../shared/types/dashboardTypes';
import { SocialConnections } from '../../../shared/types/authTypes';
import { selectFirstConnectedSocial } from '../../../store/selectors/authSelectors';
import Button from '../../../shared/ui/button/Button';
import { useNavigate } from 'react-router-dom';

const timeOptions = [
    'last 7 days',
    'last 30 days',
    'last 90 days',
    'last 180 days',
    'last 365 days',
];

type AudienceInsightsProps = {
    userSocialConnections: SocialConnections;
    selectedSocialMedia: string | null;
};

const AudienceInsights: React.FC<AudienceInsightsProps> = ({
    userSocialConnections,
    selectedSocialMedia,
}) => {
    const navigate = useNavigate();
    const firstConnectedSocial = useSelector(selectFirstConnectedSocial);
    const [selectedPlatform, setSelectedPlatform] = useState(selectedSocialMedia);
    const [selectedTime, setSelectedTime] = useState('Last 30 days');
    const dispatch = useAppDispatch();
    const brandVoiceGraph = useSelector(selectBrandVoiceGraph);
    const followersGraph = useSelector(selectFollowersGraph);
    const updatedSocialMediaOptions = socialMediaOptions.map((option) => ({
        ...option,
        disabled: !userSocialConnections?.[option.value as keyof SocialConnections],
    }));
    const brandVoiceData = useMemo(() => {
        if (!brandVoiceGraph) return [];

        const config = [
            { key: 'post_frequency', field: 'posts', value: (t: any) => t.posts_count },
            { key: 'follow_trend', field: 'followers', value: (t: any) => t.followers_end },
        ];

        const newData: Record<string, any> = {};

        config.forEach(({ key, field, value }) => {
            const arr = brandVoiceGraph[key as keyof BrandVoiceGraph];
            if (!arr) return;

            arr.forEach((trend: any) => {
                const label = trend.week;
                if (!newData[label]) {
                    newData[label] = { label };
                }
                newData[label][field] = value(trend);
            });
        });

        return Object.values(newData);
    }, [brandVoiceGraph]);

    const donutData = useMemo(() => {
        if (!followersGraph) {
            return [
                { name: 'Unfollows', value: 0, color: '#FFC400' },
                { name: 'New followers', value: 0, color: '#A053FF' },
            ];
        }

        return [
            { name: 'Unfollows', value: followersGraph.unfollows, color: '#FFC400' },
            { name: 'New followers', value: followersGraph.follows, color: '#A053FF' },
        ];
    }, [followersGraph]);

    const getDaysFromTimeRange = useCallback((timeRange: string): number => {
        switch (timeRange.toLowerCase()) {
            case 'last 7 days':
                return 7;
            case 'last 30 days':
                return 30;
            case 'last 90 days':
                return 90;
            case 'last 180 days':
                return 180;
            case 'last 365 days':
                return 365;
            default:
                console.warn('⚠️ Unknown time range:', timeRange, 'using default 30 days');
                return 30;
        }
    }, []);

    useEffect(() => {
        if (!selectedPlatform) return;

        const days = getDaysFromTimeRange(selectedTime);
        const endDate = new Date().toISOString().split('T')[0];
        const startDate = new Date(Date.now() - days * 24 * 60 * 60 * 1000)
            .toISOString()
            .split('T')[0];

        dispatch(fetchBrandVoiceGraph({ platform: selectedPlatform, startDate, endDate }));
        dispatch(fetchFollowersGraph({ platform: selectedPlatform, startDate, endDate }));
    }, [selectedPlatform, dispatch, selectedTime]);

    return (
        <div className={s.container}>
            <div className={s.headerRow}>
                <h3 className={s.title}>Audience & Content insights</h3>
                <div className={s.filters}>
                    <DropDownMenu
                        width={150}
                        options={updatedSocialMediaOptions}
                        selectedOption={selectedPlatform}
                        setSelectedOption={setSelectedPlatform}
                    />
                    <DropDownMenu
                        width={160}
                        options={timeOptions.map((time) => ({
                            value: time,
                            disabled: false,
                        }))}
                        selectedOption={selectedTime}
                        setSelectedOption={setSelectedTime}
                        icon={<IconCalendar />}
                    />
                </div>
            </div>

            {selectedPlatform ? (
                <div className={s.grid}>
                    <BrandVoiceChart data={brandVoiceData} />
                    {/* <PostTypeEngagementChart data={barData} /> */}
                    <AudienceGrowthDonut data={donutData} />
                </div>
            ) : (
                <div className={s.no_platform_selected}>
                    <p className={s.no_platform_selected_title}>No platform selected</p>
                    <p className={s.no_platform_selected_description}>
                        Please select or connect a platform to view insights
                    </p>
                    <Button
                        onClick={() => {
                            navigate('/settings');
                        }}
                        text="Connect platform"
                        style={{
                            width: '200px',
                            marginTop: '20px',
                            color: '#ffffff',
                            background: 'linear-gradient(90deg, #8801FF 0%, #D400FF 100%)',
                        }}
                    />
                </div>
            )}
        </div>
    );
};

export default AudienceInsights;
